import { z } from 'zod'
import noLeadingSpaces from '../../../utilities/noLeadingSpaces'

export const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png']

export const formSchema = z.object({
  // Contact Information
  startup_name: z.string().min(1, 'Required').refine(noLeadingSpaces, {
    message: 'Leading spaces are not allowed',
  }),
  legal_entity_type: z.string().min(1, 'Required'),
  registration_date: z.date(),
  website: z.string().optional(),

  // Personal Information
  contact_name: z.string().min(1, 'Required'),
  contact_designation: z.string().min(1, 'Required'),
  contact_email: z
    .string()
    .min(1, 'Required')
    .refine(noLeadingSpaces, {
      message: 'Leading spaces are not allowed',
    })
    .superRefine((value, ctx) => {
      if (!/^[\w-.+]+@([\w-]+\.)+[\w-]{2,4}$/.test(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid email format.',
        })
      }
    }),
  contact_phone: z
    .string()
    .min(10, 'Phone number must be 10 digits')
    .max(10, 'Phone number must be 10 digits')
    .refine((val) => /^\d{10}$/.test(val), {
      message: 'Enter a valid 10-digit phone number',
    }),

  // Address Information
  address_street: z.string().min(1, 'Required'),
  address_city: z.string().min(1, 'Required'),
  address_state: z.string().min(1, 'Required'),
  address_zip: z.string().refine((val) => /^\d{5,6}$/.test(val), {
    message: 'Enter a valid 5 or 6 digit PIN/Zip code',
  }),
  address_country: z.string().min(1, 'Required'),

  // About Startup
  industry_sector: z.string().min(1, 'Required'),
  startup_stage: z.string().min(1, 'Required'),
  business_description: z.string().min(1, 'Required'),

  // Incubation Needs
  annual_turnover: z.string().min(1, 'Required'),
  problem_statement: z.string().min(1, 'Required'),

  additional_comments: z.string().optional(),
  solution: z.string().optional(),
  unique_value_proposition: z.string().optional(),
  target_market: z.string().optional(),
  revenue_model: z.string().optional(),
  service_interest: z.string().optional(),
  previous_programs: z.string().optional(),
  declaration_agreed: z
    .union([z.literal('true'), z.literal('false'), z.boolean()])
    .transform((val) => val === 'true' || val === true),

  declaration_date: z.date().optional(), // ISO format or date input value
  signature: z
    .instanceof(File)
    .optional()
    .nullable()
    .refine((file) => !file || ACCEPTED_FILE_TYPES.includes(file.type), {
      message: 'Only image files (.jpeg, .jpg, .png) are accepted',
    })
    .refine(
      (file) => !file || file.size <= 5_000_000, // 5MB limit
      {
        message: 'File size must be less than 5MB',
      }
    ),
})

export type IncubationFormSchema = z.infer<typeof formSchema>
